import React, { useState, useEffect } from 'react';
import { UnityData, UnityConfiguration } from '../../types';
import { googleSheetsService } from '../../google-sheets-service';
import { GOOGLE_CONFIG } from '../../app-config';
import { unityBridge } from '../../unity-bridge';

interface DebugTabProps {
  unityData: UnityData | null;
}

export const DebugTab: React.FC<DebugTabProps> = ({ unityData }) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [unityConfig, setUnityConfig] = useState<UnityConfiguration | null>(null);

  useEffect(() => {
    addLog('Debug tab initialized');

    // Register callback to receive Unity configuration
    unityBridge.onConfigReceived((config) => {
      addLog(`Unity configuration received: ${config.tableSheets.length} tables configured`);
      setUnityConfig(config);
    });

    // Check if config already exists
    const existingConfig = unityBridge.getUnityConfig();
    if (existingConfig) {
      setUnityConfig(existingConfig);
    }
  }, []);

  useEffect(() => {
    if (unityData) {
      addLog(`Unity data received: ${unityData.textEntries?.length || 0} entries`);
      if (unityData.slideNumber !== undefined) {
        addLog(`Current slide: ${unityData.slideNumber} of ${unityData.totalSlides || '?'}`);
      }
    }
  }, [unityData]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const testUnityConnection = () => {
    addLog('Testing Unity connection...');
    if (window.unityInstance) {
      try {
        window.unityInstance.SendMessage('BrowserBridge', 'RequestSceneText', '');
        addLog('Message sent to Unity successfully');
      } catch (error) {
        addLog(`Failed to send message to Unity: ${error}`);
      }
    } else {
      addLog('Unity instance not found');
    }
  };

  const requestUnityConfig = () => {
    addLog('Requesting Unity configuration...');
    try {
      unityBridge.requestUnityConfig();
      addLog('Configuration request sent to Unity');
    } catch (error) {
      addLog(`Failed to request Unity configuration: ${error}`);
    }
  };

  const testGoogleSheetsConnection = async () => {
    addLog('Testing Google Sheets connection...');
    try {
      const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;
      const tableSheets = (GOOGLE_CONFIG as any).tableSheets;

      if (!masterSpreadsheetId || !tableSheets) {
        addLog('✗ No master spreadsheet configured');
        return;
      }

      // Test master spreadsheet access
      addLog(`Testing master spreadsheet: ${masterSpreadsheetId}`);
      const metadata = await googleSheetsService.getSheetMetadata(masterSpreadsheetId);
      addLog(`✓ Master spreadsheet accessible: ${metadata.title}`);

      // Test each configured sheet
      for (const [tableName, sheetConfig] of Object.entries(tableSheets)) {
        const config = sheetConfig as { sheetId: number; sheetName: string };
        addLog(`Checking sheet for table: ${tableName} (${config.sheetName}, gid: ${config.sheetId})`);

        const sheetExists = metadata.sheets.some(s => s.sheetId === config.sheetId || s.title === config.sheetName);
        if (sheetExists) {
          addLog(`✓ Sheet for "${tableName}" found in master spreadsheet`);
        } else {
          addLog(`✗ Sheet for "${tableName}" NOT found in master spreadsheet`);
        }
      }
    } catch (error) {
      addLog(`Google Sheets connection failed: ${error}`);
    }
  };

  const clearLogs = () => {
    setLogs([]);
    addLog('Logs cleared');
  };

  const exportDebugInfo = () => {
    const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;
    const tableSheets = (GOOGLE_CONFIG as any).tableSheets;

    const debugInfo = {
      timestamp: new Date().toISOString(),
      googleConfig: {
        masterSpreadsheet: masterSpreadsheetId || null,
        tableSheets: tableSheets ? Object.entries(tableSheets).map(([table, sheet]) => ({
          table,
          sheetName: (sheet as any).sheetName,
          sheetId: (sheet as any).sheetId
        })) : null
      },
      unityConfig: unityConfig,
      unityData: unityData,
      logs: logs
    };

    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    addLog('Debug info exported');
  };

  return (
    <div className="flex flex-col h-full p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Debug Console</h2>
        <div className="flex gap-2">
          <button
            onClick={clearLogs}
            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={exportDebugInfo}
            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Export
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 rounded p-3">
          <h3 className="font-semibold mb-2">app-config.js (Browser Configuration)</h3>
          <div className="text-xs space-y-1">
            <div className="mb-2 pb-2 border-b border-gray-300">
              <span className="font-medium">Master Spreadsheet:</span>{' '}
              <span className="font-mono text-gray-600">
                {((GOOGLE_CONFIG as any).masterSpreadsheet as string)?.substring(0, 30) || 'Not configured'}
              </span>
            </div>

            {Object.entries((GOOGLE_CONFIG as any).tableSheets || {}).map(([tableName, sheetConfig]: [string, any], idx) => (
              <div key={idx}>
                <span className="font-medium">{tableName}:</span>{' '}
                <span className="text-gray-600">{sheetConfig.sheetName}</span>{' '}
                <span className="font-mono text-gray-500">(gid: {sheetConfig.sheetId})</span>
              </div>
            ))}
            {(GOOGLE_CONFIG as any).referencesTable && (
              <div>
                <span className="font-medium">References:</span>{' '}
                <span className="text-gray-600">{(GOOGLE_CONFIG as any).referencesTable.sheetName}</span>{' '}
                <span className="font-mono text-gray-500">(gid: {(GOOGLE_CONFIG as any).referencesTable.sheetId})</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded p-3">
          <h3 className="font-semibold mb-2">Test Actions</h3>
          <div className="space-y-2">
            <button
              onClick={testUnityConnection}
              className="w-full px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
            >
              Test Unity Connection
            </button>
            <button
              onClick={testGoogleSheetsConnection}
              className="w-full px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
            >
              Test Google Sheets Access
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 border rounded bg-black text-green-400 p-3 overflow-auto font-mono text-xs">
        {logs.map((log, idx) => (
          <div key={idx} className="hover:bg-gray-900">
            {log}
          </div>
        ))}
      </div>
    </div>
  );
};
